if (!customElements.get('people-directory')) {
  customElements.define(
    'people-directory',
    class PeopleDirectory extends HTMLElement {
      constructor() {
        super();
        this.people = [];
        this.editingIndex = -1;
        this.maxPeople = parseInt(this.dataset.maxPeople) || 1;
        this.productFormId = this.dataset.productFormId;
        this.optionName = this.dataset.optionName || 'Number of People';
        this.assetBase = this.dataset.assetBase || '';
        this.hairType = this.dataset.hairType || 'type1';
        this.shirtType = this.dataset.shirtType || 'type1';
      }

      connectedCallback() {
        // Load catalog data
        const catalogEl = this.querySelector('[data-pd-catalog]');
        this.catalog = catalogEl ? JSON.parse(catalogEl.textContent) : { hair: {}, shirt: {} };

        // DOM refs
        this.formEl = this.querySelector('.people-directory__form');
        this.listEl = this.querySelector('.people-directory__list');
        this.messageEl = this.querySelector('.people-directory__message');
        this.addBtn = this.querySelector('[data-action="add"]');
        this.cancelBtn = this.querySelector('[data-action="cancel"]');
        this.formTitleText = this.querySelector('[data-form-title-text]');
        this.subheadingEl = this.querySelector('.people-directory__subheading');

        // Input fields
        this.nameInput = this.querySelector('[data-field="name"]');
        this.ageInput = this.querySelector('[data-field="age"]');
        this.heightFeetInput = this.querySelector('[data-field="heightFeet"]');
        this.heightInchesInput = this.querySelector('[data-field="heightInches"]');
        this.skinInput = this.querySelector('[data-field="skin"]');
        this.skinColorInput = this.querySelector('[data-field="skinColor"]');
        this.skinNameInput = this.querySelector('[data-field="skinName"]');
        this.hairInput = this.querySelector('[data-field="hair"]');
        this.hairCodeInput = this.querySelector('[data-field="hairCode"]');
        this.shirtInput = this.querySelector('[data-field="shirt"]');
        this.shirtCodeInput = this.querySelector('[data-field="shirtCode"]');
        this.notesInput = this.querySelector('[data-field="notes"]');

        // Skin swatches
        this.skinSwatches = this.querySelector('[data-skin-swatches]');

        // Image selectors
        this.hairSelector = this.querySelector('[data-selector="hair"]');
        this.shirtSelector = this.querySelector('[data-selector="shirt"]');

        // Popup
        this.popup = this.querySelector('[data-popup]');
        this.popupTitle = this.querySelector('[data-popup-title]');
        this.popupTabs = this.querySelector('[data-popup-tabs]');
        this.popupSubtabs = this.querySelector('[data-popup-subtabs]');
        this.popupGrid = this.querySelector('[data-popup-grid]');

        // Active popup state
        this.activePopupType = null;
        this.activeTab = null;
        this.activeSubtab = null;

        // Event listeners
        this.addBtn.addEventListener('click', this.handleAdd.bind(this));
        this.cancelBtn.addEventListener('click', this.handleCancel.bind(this));
        this.hairSelector.addEventListener('click', () => this.openPopup('hair'));
        this.shirtSelector.addEventListener('click', () => this.openPopup('shirt'));

        // Popup close listeners
        this.popup.querySelectorAll('[data-popup-close]').forEach((el) => {
          el.addEventListener('click', () => this.closePopup());
        });

        // Skin swatch listeners
        this.skinSwatches.querySelectorAll('.people-directory__skin-swatch').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.selectSkin(btn.dataset.skinCode, btn.dataset.skinColor, btn.dataset.skinName);
          });
        });

        // Real-time form listeners for button state
        this.setupFormListeners();

        // Variant change listener
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.handleVariantChange.bind(this));

        this.detectInitialMax();
        this.render();
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      // ── Form listeners for real-time button state ──

      setupFormListeners() {
        // Listen for changes on required fields to enable/disable Add button
        this.nameInput.addEventListener('input', () => this.updateAddButtonState());
      }

      updateAddButtonState() {
        const isEditing = this.editingIndex >= 0;
        const isFull = this.people.length >= this.maxPeople && !isEditing;
        if (isFull) {
          this.addBtn.disabled = true;
          return;
        }
        // Enable only when name, skin, hair, and shirt are provided
        const hasName = this.nameInput.value.trim().length > 0;
        const hasSkin = this.skinInput.value.trim().length > 0;
        const hasHair = this.hairInput.value.trim().length > 0;
        const hasShirt = this.shirtInput.value.trim().length > 0;
        this.addBtn.disabled = !(hasName && hasSkin && hasHair && hasShirt);
      }

      // ── Skin swatch selection ──

      selectSkin(code, color, name) {
        this.skinInput.value = code;
        this.skinColorInput.value = color;
        this.skinNameInput.value = name;

        // Update visual state
        this.skinSwatches.querySelectorAll('.people-directory__skin-swatch').forEach((s) => {
          s.classList.toggle('is-active', s.dataset.skinCode === code);
        });
        this.skinSwatches.classList.remove('invalid');
        this.updateAddButtonState();
      }

      // ── Variant detection ──

      detectInitialMax() {
        const variantSelects = document.querySelector('variant-selects[data-primary]');
        if (!variantSelects) return;
        const checkedInputs = variantSelects.querySelectorAll('fieldset input:checked');
        for (const input of checkedInputs) {
          const fieldset = input.closest('fieldset');
          const legend = fieldset?.querySelector('legend, .form__label');
          if (legend && this.isNumberOfPeopleOption(legend.textContent)) {
            this.updateMax(input.value);
            return;
          }
        }
        const selects = variantSelects.querySelectorAll('select');
        for (const select of selects) {
          const label = variantSelects.querySelector(`label[for="${select.id}"]`);
          if (label && this.isNumberOfPeopleOption(label.textContent)) {
            this.updateMax(select.value);
            return;
          }
        }
      }

      isNumberOfPeopleOption(text) {
        const normalized = text.toLowerCase().replace(/[^a-z0-9]/g, '');
        return normalized.includes('numberofpeople') || normalized.includes('numpeople') || normalized.includes('personas') || normalized.includes('numberpeople');
      }

      handleVariantChange(event) {
        const { target } = event.data;
        if (!target) return;
        const fieldset = target.closest('fieldset');
        const selectWrapper = target.closest('.product-form__input--dropdown');
        let optionLabel = '';
        if (fieldset) {
          const legend = fieldset.querySelector('legend, .form__label');
          if (legend) optionLabel = legend.textContent;
        } else if (selectWrapper) {
          const label = selectWrapper.querySelector('.form__label');
          if (label) optionLabel = label.textContent;
        }
        if (this.isNumberOfPeopleOption(optionLabel)) {
          this.updateMax(target.value || target.dataset?.value);
        }
      }

      updateMax(value) {
        const num = parseInt(value);
        if (!isNaN(num) && num > 0) {
          this.maxPeople = num;
          if (this.people.length > this.maxPeople) {
            this.people = this.people.slice(0, this.maxPeople);
            if (this.editingIndex >= this.maxPeople) {
              this.editingIndex = -1;
              this.clearForm();
            }
          }
          this.render();
        }
      }

      // ── Popup ──

      getTypedCatalog(type) {
        const typeKey = type === 'hair' ? this.hairType : this.shirtType;
        const typeData = this.catalog[type];
        if (typeData && typeData[typeKey]) return typeData[typeKey];
        if (typeData && typeData['type1']) return typeData['type1'];
        return typeData || {};
      }

      openPopup(type) {
        this.activePopupType = type;
        const data = this.getTypedCatalog(type);
        const tabs = Object.keys(data);

        this.popupTitle.textContent = type === 'hair' ? 'Select Hair Style' : 'Select Shirt Style';
        this.activeTab = tabs[0];

        this.popupTabs.innerHTML = tabs.map((tab) =>
          `<button type="button" class="pd-popup__tab${tab === this.activeTab ? ' is-active' : ''}" data-tab="${this.escapeAttr(tab)}">${this.escapeHtml(tab)}</button>`
        ).join('');

        this.popupTabs.querySelectorAll('.pd-popup__tab').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.activeTab = btn.dataset.tab;
            this.popupTabs.querySelectorAll('.pd-popup__tab').forEach((b) => b.classList.remove('is-active'));
            btn.classList.add('is-active');
            this.renderSubtabs();
            this.renderGrid();
          });
        });

        this.renderSubtabs();
        this.renderGrid();

        this.popup.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      }

      closePopup() {
        this.popup.classList.remove('is-open');
        document.body.style.overflow = '';
        this.activePopupType = null;
      }

      renderSubtabs() {
        const data = this.getTypedCatalog(this.activePopupType);
        const subtabs = Object.keys(data[this.activeTab] || {});
        this.activeSubtab = subtabs[0] || null;

        if (subtabs.length <= 1) {
          this.popupSubtabs.innerHTML = '';
          return;
        }

        this.popupSubtabs.innerHTML = subtabs.map((st) =>
          `<button type="button" class="pd-popup__subtab${st === this.activeSubtab ? ' is-active' : ''}" data-subtab="${this.escapeAttr(st)}">${this.escapeHtml(st)}</button>`
        ).join('');

        this.popupSubtabs.querySelectorAll('.pd-popup__subtab').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.activeSubtab = btn.dataset.subtab;
            this.popupSubtabs.querySelectorAll('.pd-popup__subtab').forEach((b) => b.classList.remove('is-active'));
            btn.classList.add('is-active');
            this.renderGrid();
          });
        });
      }

      renderGrid() {
        const data = this.getTypedCatalog(this.activePopupType);
        const tabData = data[this.activeTab] || {};
        const items = this.activeSubtab ? (tabData[this.activeSubtab] || []) : Object.values(tabData).flat();

        const currentValue = this.activePopupType === 'hair' ? this.hairInput.value : this.shirtInput.value;

        this.popupGrid.innerHTML = items.map((item) => {
          const isSelected = item.file === currentValue;
          return `
            <button type="button" class="pd-popup__item${isSelected ? ' is-selected' : ''}" data-file="${this.escapeAttr(item.file)}" data-code="${this.escapeAttr(item.code)}">
              <img src="${this.assetBase}${item.file}" alt="${this.escapeAttr(item.code || item.file)}" loading="lazy" />
              <span class="pd-popup__item-check">
                <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor"><path d="M16.7 5.3a1 1 0 0 1 0 1.4l-8 8a1 1 0 0 1-1.4 0l-4-4a1 1 0 1 1 1.4-1.4L8 12.6l7.3-7.3a1 1 0 0 1 1.4 0z"/></svg>
              </span>
            </button>
          `;
        }).join('');

        this.popupGrid.querySelectorAll('.pd-popup__item').forEach((btn) => {
          btn.addEventListener('click', () => this.selectItem(btn.dataset.file, btn.dataset.code));
        });
      }

      selectItem(file, code) {
        if (this.activePopupType === 'hair') {
          this.hairInput.value = file;
          this.hairCodeInput.value = code;
          this.updateSelectorPreview(this.hairSelector, file);
        } else {
          this.shirtInput.value = file;
          this.shirtCodeInput.value = code;
          this.updateSelectorPreview(this.shirtSelector, file);
        }
        this.updateAddButtonState();
        this.closePopup();
      }

      updateSelectorPreview(selector, file) {
        const preview = selector.querySelector('.people-directory__image-selector-preview');
        const text = selector.querySelector('.people-directory__image-selector-text');
        selector.classList.remove('invalid');

        if (file) {
          preview.innerHTML = `<img src="${this.assetBase}${file}" alt="Selected" />`;
          const label = this.fileToLabel(file);
          text.textContent = label;
          text.classList.add('has-value');
        } else {
          preview.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="8" r="4"/><path d="M5 20c0-4 3.5-7 7-7s7 3 7 7"/></svg>`;
          text.textContent = selector.dataset.selector === 'hair' ? 'Choose a hair style...' : 'Choose a shirt style...';
          text.classList.remove('has-value');
        }
      }

      fileToLabel(file) {
        if (file.startsWith('pd-hair-')) {
          const rest = file.replace('pd-hair-', '').replace('.png', '');
          const withoutType = rest.replace(/^type\d+-/, '');
          const match = withoutType.match(/^(female|male)-(.+?)-(hm?\d+)$/);
          if (match) {
            const gender = match[1].charAt(0).toUpperCase() + match[1].slice(1);
            const style = match[2].replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
            return `${gender} - ${style} (${match[3].toUpperCase()})`;
          }
        }
        if (file.startsWith('pd-shirt-')) {
          const rest = file.replace('pd-shirt-', '').replace('.png', '');
          const withoutType = rest.replace(/^type\d+-/, '');
          const match = withoutType.match(/^(\w+)-(tshirt|turtleneck)-(.+)$/);
          if (match) {
            const cat = match[1].charAt(0).toUpperCase() + match[1].slice(1);
            const type = match[2] === 'tshirt' ? 'T-Shirt' : 'Turtle Neck';
            const detail = match[3].replace(/-/, ' - ').toUpperCase();
            return `${cat} - ${type} (${detail})`;
          }
        }
        return file;
      }

      // ── Add / Edit / Delete ──

      handleAdd() {
        const person = this.getFormValues();
        if (!this.validate(person)) return;

        if (this.editingIndex >= 0) {
          this.people[this.editingIndex] = person;
          this.editingIndex = -1;
        } else {
          if (this.people.length >= this.maxPeople) {
            this.showMessage('error', `You can only add ${this.maxPeople} ${this.maxPeople === 1 ? 'person' : 'people'}.`);
            return;
          }
          this.people.push(person);
        }

        this.clearForm();
        this.hideMessage();
        this.render();
      }

      handleCancel() {
        this.editingIndex = -1;
        this.clearForm();
        this.hideMessage();
        this.render();
      }

      handleEdit(index) {
        const person = this.people[index];
        this.nameInput.value = person.name;
        this.ageInput.value = person.age;
        this.heightFeetInput.value = person.heightFeet;
        this.heightInchesInput.value = person.heightInches;
        this.skinInput.value = person.skin;
        this.skinColorInput.value = person.skinColor;
        this.skinNameInput.value = person.skinName;
        this.hairInput.value = person.hair;
        this.hairCodeInput.value = person.hairCode;
        this.shirtInput.value = person.shirt;
        this.shirtCodeInput.value = person.shirtCode;
        this.notesInput.value = person.notes || '';

        // Set active skin swatch
        this.skinSwatches.querySelectorAll('.people-directory__skin-swatch').forEach((s) => {
          s.classList.toggle('is-active', s.dataset.skinCode === person.skin);
        });

        // Update image selectors
        this.updateSelectorPreview(this.hairSelector, person.hair);
        this.updateSelectorPreview(this.shirtSelector, person.shirt);

        this.editingIndex = index;
        this.render();
        this.formEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      handleDelete(index) {
        this.people.splice(index, 1);
        if (this.editingIndex === index) {
          this.editingIndex = -1;
          this.clearForm();
        } else if (this.editingIndex > index) {
          this.editingIndex--;
        }
        this.render();
      }

      getFormValues() {
        return {
          name: this.nameInput.value.trim(),
          age: this.ageInput.value.trim(),
          heightFeet: this.heightFeetInput.value.trim(),
          heightInches: this.heightInchesInput.value.trim(),
          skin: this.skinInput.value.trim(),
          skinColor: this.skinColorInput.value.trim(),
          skinName: this.skinNameInput.value.trim(),
          hair: this.hairInput.value.trim(),
          hairCode: this.hairCodeInput.value.trim(),
          shirt: this.shirtInput.value.trim(),
          shirtCode: this.shirtCodeInput.value.trim(),
          notes: this.notesInput.value.trim(),
        };
      }

      validate(person) {
        let valid = true;

        // Name
        if (!person.name) { this.nameInput.classList.add('invalid'); valid = false; }
        else { this.nameInput.classList.remove('invalid'); }

        // Skin
        if (!person.skin) { this.skinSwatches.classList.add('invalid'); valid = false; }
        else { this.skinSwatches.classList.remove('invalid'); }

        // Hair
        if (!person.hair) { this.hairSelector.classList.add('invalid'); valid = false; }
        else { this.hairSelector.classList.remove('invalid'); }

        // Shirt
        if (!person.shirt) { this.shirtSelector.classList.add('invalid'); valid = false; }
        else { this.shirtSelector.classList.remove('invalid'); }

        if (!valid) {
          this.showMessage('error', 'Please fill in the name and select skin tone, hair style, and shirt style.');
        }
        return valid;
      }

      clearForm() {
        this.nameInput.value = '';
        this.ageInput.value = '';
        this.heightFeetInput.value = '';
        this.heightInchesInput.value = '';
        this.skinInput.value = '';
        this.skinColorInput.value = '';
        this.skinNameInput.value = '';
        this.hairInput.value = '';
        this.hairCodeInput.value = '';
        this.shirtInput.value = '';
        this.shirtCodeInput.value = '';
        this.notesInput.value = '';

        // Clear skin swatch selection
        this.skinSwatches.querySelectorAll('.people-directory__skin-swatch').forEach((s) => {
          s.classList.remove('is-active');
        });
        this.skinSwatches.classList.remove('invalid');

        this.updateSelectorPreview(this.hairSelector, '');
        this.updateSelectorPreview(this.shirtSelector, '');

        this.nameInput.classList.remove('invalid');
        this.hairSelector.classList.remove('invalid');
        this.shirtSelector.classList.remove('invalid');

        this.updateAddButtonState();
      }

      showMessage(type, text) {
        this.messageEl.textContent = text;
        this.messageEl.className = 'people-directory__message people-directory__message--' + type;
      }

      hideMessage() {
        this.messageEl.className = 'people-directory__message';
        this.messageEl.textContent = '';
      }

      getInitials(name) {
        return name.split(' ').map((w) => w[0]).join('').substring(0, 2).toUpperCase();
      }

      formatHeight(feet, inches) {
        if (!feet && !inches) return '';
        const ft = feet || '0';
        const inc = inches || '0';
        return `${ft}' ${inc}"`;
      }

      // ── Rendering ──

      render() {
        this.renderList();
        this.renderHiddenInputs();
        this.renderFormState();
        this.updateValidation();
      }

      renderFormState() {
        const isEditing = this.editingIndex >= 0;
        const isFull = this.people.length >= this.maxPeople && !isEditing;

        if (this.formTitleText) {
          this.formTitleText.textContent = isEditing ? `Editing Person ${this.editingIndex + 1}` : 'New Person';
        }

        this.addBtn.textContent = isEditing ? 'Save Changes' : 'Add Person';
        this.cancelBtn.style.display = isEditing ? '' : 'none';

        const buttonsWrapper = this.querySelector('.people-directory__form-buttons');
        if (buttonsWrapper) {
          buttonsWrapper.style.display = 'flex';
          buttonsWrapper.style.gap = isEditing ? '0.8rem' : '0';
        }

        if (this.subheadingEl) {
          this.subheadingEl.textContent = `Add details for each person in your artwork (${this.people.length}/${this.maxPeople}).`;
        }

        this.updateAddButtonState();
      }

      renderList() {
        if (this.people.length === 0) {
          this.listEl.innerHTML = '';
          return;
        }

        let html = `
          <div class="people-directory__list-header">
            <span class="people-directory__list-title">People Added</span>
            <span class="people-directory__count-badge">${this.people.length} of ${this.maxPeople}</span>
          </div>
        `;

        this.people.forEach((person, i) => {
          const heightStr = this.formatHeight(person.heightFeet, person.heightInches);
          const detailParts = [];
          if (person.age) detailParts.push(`Age ${this.escapeHtml(person.age)}`);
          if (heightStr) detailParts.push(`height ${this.escapeHtml(heightStr)}`);
          const detailStr = detailParts.join(' &amp; ');

          const hairThumb = person.hair ? `<img class="people-directory__person-preview-img" src="${this.assetBase}${this.escapeAttr(person.hair)}" alt="Hair" />` : '';
          const shirtThumb = person.shirt ? `<img class="people-directory__person-preview-img" src="${this.assetBase}${this.escapeAttr(person.shirt)}" alt="Shirt" />` : '';
          const skinSwatch = person.skinColor ? `<span class="people-directory__person-preview-skin" style="background-color: ${this.escapeAttr(person.skinColor)}" title="${this.escapeAttr(person.skinName || '')}"></span>` : '';

          html += `
            <div class="people-directory__person" data-index="${i}">
              <div class="people-directory__person-avatar" style="background-color: ${this.escapeAttr(person.skinColor || '#ccc')}">
                ${this.getInitials(person.name)}
              </div>
              <div class="people-directory__person-info">
                <div class="people-directory__person-name">${this.escapeHtml(person.name)}</div>
                ${detailStr ? `<div class="people-directory__person-details">${detailStr}</div>` : ''}
                ${person.notes ? `<div class="people-directory__person-notes">${this.escapeHtml(person.notes)}</div>` : ''}
              </div>
              <div class="people-directory__person-previews">
                ${hairThumb}${shirtThumb}${skinSwatch}
              </div>
              <div class="people-directory__person-actions">
                <button type="button" class="people-directory__action-btn people-directory__action-btn--edit" data-edit="${i}" aria-label="Edit ${this.escapeAttr(person.name)}">
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14.85 2.85a1.2 1.2 0 0 1 1.7 0l.6.6a1.2 1.2 0 0 1 0 1.7L7.5 14.8l-3.3.8.8-3.3z"/></svg>
                </button>
                <button type="button" class="people-directory__action-btn people-directory__action-btn--delete" data-delete="${i}" aria-label="Delete ${this.escapeAttr(person.name)}">
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M5 5h10M8 5V3h4v2M6 5v11a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V5"/></svg>
                </button>
              </div>
            </div>
          `;
        });

        html += `<div class="people-directory__status">Displaying ${this.people.length} of ${this.maxPeople} ${this.maxPeople === 1 ? 'person' : 'people'}</div>`;

        this.listEl.innerHTML = html;

        this.listEl.querySelectorAll('[data-edit]').forEach((btn) => {
          btn.addEventListener('click', () => this.handleEdit(parseInt(btn.dataset.edit)));
        });
        this.listEl.querySelectorAll('[data-delete]').forEach((btn) => {
          btn.addEventListener('click', () => this.handleDelete(parseInt(btn.dataset.delete)));
        });
      }

      renderHiddenInputs() {
        const form = document.getElementById(this.productFormId);
        if (!form) return;

        form.querySelectorAll('[data-people-directory]').forEach((el) => el.remove());

        this.people.forEach((person, i) => {
          const num = i + 1;
          const heightStr = this.formatHeight(person.heightFeet, person.heightInches);
          const fields = {
            [`Person ${num} - Name`]: person.name,
            [`Person ${num} - Age`]: person.age,
            [`Person ${num} - Height`]: heightStr,
            [`Person ${num} - Skin Tone`]: person.skin,
            [`Person ${num} - Hair`]: person.hairCode,
            [`Person ${num} - Shirt`]: person.shirtCode,
          };
          // Only add Notes if provided
          if (person.notes) {
            fields[`Person ${num} - Notes`] = person.notes;
          }

          Object.entries(fields).forEach(([key, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = `properties[${key}]`;
            input.value = value;
            input.setAttribute('data-people-directory', '');
            form.appendChild(input);
          });
        });
      }

      updateValidation() {
        let validator = this.querySelector('.people-directory__validator');
        if (!validator) {
          validator = document.createElement('input');
          validator.type = 'hidden';
          validator.className = 'people-directory__validator';
          validator.setAttribute('form', this.productFormId);
          this.appendChild(validator);
        }

        if (this.people.length >= this.maxPeople) {
          validator.removeAttribute('required');
          validator.value = 'complete';
        } else {
          validator.setAttribute('required', '');
          validator.value = '';
        }
      }

      escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
      }

      escapeAttr(str) {
        return str.replace(/[&"'<>]/g, (c) => ({
          '&': '&amp;', '"': '&quot;', "'": '&#39;', '<': '&lt;', '>': '&gt;',
        }[c]));
      }
    }
  );
}
